import type { Person, SchemaOptions } from '@crmsy/shared';
import { filtersEmpty, EMPTY_FILTERS, toggleWindow, type Filters, type ListFilterKey } from '../lib/filters.js';
import { useT, type Key } from '../i18n/index.js';

export interface FilterChipsProps {
  filters: Filters;
  setFilters: (f: Filters) => void;
  schema: SchemaOptions;
  people: Person[];
  openPicker: (key: ListFilterKey, anchor: { x: number; y: number }) => void;
}

const LIST_CHIPS: { key: ListFilterKey; label: Key; hotkey: string }[] = [
  { key: 'stage', label: 'chip.stage', hotkey: 's' },
  { key: 'nextAction', label: 'chip.action', hotkey: 'a' },
  { key: 'status', label: 'chip.status', hotkey: 't' },
  { key: 'owner', label: 'chip.owner', hotkey: 'o' },
];

const FLAG_CHIPS: { key: 'mine' | 'due' | 'week' | 'month' | 'archived'; label: Key; hotkey: string }[] = [
  { key: 'mine', label: 'chip.mine', hotkey: 'i' },
  { key: 'due', label: 'chip.due', hotkey: 'd' },
  { key: 'week', label: 'chip.week', hotkey: 'w' },
  { key: 'month', label: 'chip.month', hotkey: 'm' },
  { key: 'archived', label: 'chip.archived', hotkey: '' },
];

/** Filter chips above the table (§6.3). Each chip carries its shortcut letter (§7); an active list chip has its own ✕. */
export function FilterChips({ filters, setFilters, people, openPicker }: FilterChipsProps) {
  const t = useT();
  const personName = (id: string) => people.find((p) => p.id === id)?.name ?? '?';
  const summary = (key: ListFilterKey) => {
    const v = filters[key];
    if (!v.length) return null;
    const names = key === 'owner' ? v.map(personName) : v;
    return names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {LIST_CHIPS.map((c) => {
        const on = filters[c.key].length > 0;
        const label = t(c.label);
        return (
          <span key={c.key} className="chip p-0" data-filter-key={c.key} data-on={on}>
            <button
              type="button"
              className="inline-flex items-center gap-1 py-1 pl-2.5 pr-1.5"
              onClick={(e) => {
                const r = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
                openPicker(c.key, { x: r.left, y: r.bottom + 6 });
              }}
            >
              <kbd>{c.hotkey}</kbd>
              {label}
              {on && <span className="max-w-40 truncate">: {summary(c.key)}</span>}
              {!on && <span className="text-muted">▾</span>}
            </button>
            {on && (
              <button
                type="button"
                aria-label={t('chip.clearOne', { name: label })}
                title={t('chip.clearOne', { name: label })}
                className="rounded-r-full py-1 pl-0.5 pr-2 text-muted hover:text-danger"
                onClick={() => setFilters({ ...filters, [c.key]: [] })}
              >
                ✕
              </button>
            )}
          </span>
        );
      })}
      <span className="mx-1 h-4 w-px bg-line" />
      {FLAG_CHIPS.map((c) => (
        <button
          key={c.key}
          type="button"
          className="chip"
          data-on={filters[c.key]}
          onClick={() => setFilters(c.key === 'due' || c.key === 'week' || c.key === 'month' ? toggleWindow(filters, c.key) : { ...filters, [c.key]: !filters[c.key] })}
        >
          {c.hotkey && <kbd>{c.hotkey}</kbd>}
          {t(c.label)}
        </button>
      ))}
      {!filtersEmpty(filters) && (
        <button type="button" className="chip" onClick={() => setFilters({ ...EMPTY_FILTERS })}>
          <kbd>x</kbd>
          {t('chip.clear')}
        </button>
      )}
    </div>
  );
}
